import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

type Step = {
  key: string;
  label: string;
  count: number;
  width: number;
  step_rate: number;
};
type MiniFunnel = { base: number; steps: Step[] };

const n = (x: number) => x.toLocaleString("ru-RU");
const LS_KEY = "dash_funnel_open";

export function MiniFunnel() {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LS_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Тянем данные только когда раздел раскрыт — не грузим счётчики на каждом
  // заходе на дашборд.
  const q = useQuery<MiniFunnel>({
    queryKey: ["mini-funnel"],
    queryFn: () => api("/api/admin/stats/mini-funnel"),
    enabled: open,
  });

  const steps = q.data?.steps ?? [];

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(LS_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  };

  return (
    <section className="mb-8">
      <button
        onClick={toggle}
        className="flex items-center gap-2 mb-3 w-full text-left group"
      >
        <span className="w-1 h-4 rounded bg-brand" />
        <span className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 group-hover:text-zinc-300 transition-colors">
          Воронка пути юзера
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
      <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 md:p-5">
        {q.isLoading && (
          <div className="text-sm text-zinc-500">Загрузка…</div>
        )}
        {!q.isLoading && steps.length > 0 && (
          <div className="flex flex-col gap-2.5">
            {steps.map((s, i) => {
              // Падение относительно предыдущего шага — где отваливаются.
              const drop = i > 0 ? 100 - s.step_rate : 0;
              return (
                <div key={s.key} className="flex items-center gap-3">
                  <div className="w-28 shrink-0 text-xs text-zinc-400 text-right">
                    {s.label}
                  </div>
                  <div className="flex-1 h-8 rounded-lg bg-white/[0.03] overflow-hidden relative">
                    <div
                      className="h-full rounded-lg bg-gradient-to-r from-brand/60 to-brand/25 transition-all"
                      style={{ width: `${Math.max(s.width, 1.5)}%` }}
                    />
                    <div className="absolute inset-0 flex items-center px-3 gap-2">
                      <span className="text-sm font-semibold tabular-nums text-zinc-100">
                        {n(s.count)}
                      </span>
                      {i > 0 && (
                        <span className="text-[11px] tabular-nums text-zinc-500">
                          {s.step_rate}% с прошлого
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-14 shrink-0 text-right">
                    {i > 0 && drop > 0 && (
                      <span className="text-[11px] tabular-nums text-red-400/70">
                        −{drop.toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-zinc-600 mt-3">
          Шаги «тарифы / выбрал / платёж» наполняются из событий по мере действий
          юзеров — на исторических данных они могут быть ниже реальных.
        </p>
      </div>
      )}
    </section>
  );
}
